import { type ElementType } from 'react';
import {
  Briefcase,
  Building2,
  FileText,
  Images,
  LayoutGrid,
  MapPin,
  MessageSquare,
  Package,
  Receipt,
  ReceiptText,
  Star,
  StickyNote,
  Wallet,
  FolderKanban,
} from 'lucide-react';
import { cn } from '@/lib/utils';

export type ContactSectionId =
  | 'summary'
  | 'locations'
  | 'communication'
  | 'estimates'
  | 'jobs'
  | 'projects'
  | 'invoices'
  | 'bill-to'
  | 'payments'
  | 'receipts'
  | 'memberships'
  | 'equipment'
  | 'media'
  | 'notes';

type CountKey = 'jobs' | 'invoices' | 'estimates';

interface NavItem {
  id: ContactSectionId;
  label: string;
  icon: ElementType;
  countKey?: CountKey;
}

interface NavGroup {
  label?: string;
  items: NavItem[];
}

/**
 * What a site contact's record is for.
 *
 * Someone attached to a location is a person to reach, not an account: they
 * hold no estimates, are sent no invoices, and buy no memberships — those
 * belong to the customer who owns the site. Showing them eight empty
 * money sections invites the reader to wonder what is missing. The nav is
 * derived, not stored, so a site contact that later owns a location or takes
 * an invoice becomes a customer and gets the full record back.
 */
export function contactNavGroups(isSiteContactOnly: boolean): NavGroup[] {
  if (!isSiteContactOnly) return GROUPS;
  return [
    { items: [
      { id: 'summary', label: 'Summary', icon: LayoutGrid },
      { id: 'locations', label: 'Locations', icon: MapPin },
    ] },
    { label: 'History', items: [
      { id: 'communication', label: 'Communication', icon: MessageSquare },
      { id: 'notes', label: 'Notes & docs', icon: StickyNote },
    ] },
  ];
}

const GROUPS: NavGroup[] = [
  {
    items: [
      { id: 'summary', label: 'Summary', icon: LayoutGrid },
      { id: 'locations', label: 'Locations', icon: MapPin },
    ],
  },
  {
    label: 'Work & money',
    items: [
      { id: 'estimates', label: 'Estimates', icon: FileText, countKey: 'estimates' },
      { id: 'jobs', label: 'Jobs', icon: Briefcase, countKey: 'jobs' },
      { id: 'projects', label: 'Projects', icon: FolderKanban },
      { id: 'invoices', label: 'Invoices', icon: ReceiptText, countKey: 'invoices' },
      { id: 'bill-to', label: 'Bill To', icon: Building2 },
      { id: 'payments', label: 'Payments', icon: Wallet },
      { id: 'receipts', label: 'Receipts', icon: Receipt },
      { id: 'memberships', label: 'Memberships', icon: Star },
    ],
  },
  {
    label: 'History',
    items: [
      { id: 'communication', label: 'Communication', icon: MessageSquare },
      { id: 'media', label: 'Media', icon: Images },
      { id: 'notes', label: 'Notes & docs', icon: StickyNote },
      { id: 'equipment', label: 'Equipment', icon: Package },
    ],
  },
];

export const CONTACT_SECTION_IDS: ContactSectionId[] = GROUPS.flatMap((group) =>
  group.items.map((item) => item.id),
);

/** Flat section list (id + label + optional count key) for the mobile tab bar. */
export const CONTACT_SECTIONS: { id: ContactSectionId; label: string; countKey?: CountKey }[] =
  GROUPS.flatMap((group) =>
    group.items.map(({ id, label, countKey }) => ({ id, label, countKey })),
  );

export type ContactSectionCountKey = CountKey;

export function ContactSectionNav({
  active,
  onSelect,
  counts,
  isSiteContactOnly = false,
}: {
  active: ContactSectionId;
  onSelect: (id: ContactSectionId) => void;
  counts?: Partial<Record<CountKey, number>>;
  isSiteContactOnly?: boolean;
}) {
  const groups = contactNavGroups(isSiteContactOnly);
  return (
    <nav className="w-[200px] shrink-0 overflow-y-auto border-r bg-muted/30 px-2.5 py-3">
      {groups.map((group, index) => (
        <div key={group.label ?? `group-${index}`}>
          {group.label ? (
            <p className="px-2.5 pb-1.5 pt-3 text-[10.5px] font-bold uppercase tracking-wider text-muted-foreground/70">
              {group.label}
            </p>
          ) : null}
          {group.items.map((item) => {
            const Icon = item.icon;
            const isActive = active === item.id;
            const count = item.countKey ? counts?.[item.countKey] : undefined;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => onSelect(item.id)}
                className={cn(
                  'flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-[13px]',
                  isActive
                    ? 'bg-muted font-semibold text-foreground shadow-[inset_2px_0_0_var(--primary)]'
                    : 'font-medium text-muted-foreground hover:bg-muted/70 hover:text-foreground',
                )}
              >
                <Icon
                  className={cn(
                    'size-[15px] shrink-0',
                    isActive ? 'text-foreground' : 'text-muted-foreground',
                  )}
                />
                <span className="min-w-0 flex-1 truncate text-left">{item.label}</span>
                {typeof count === 'number' ? (
                  <span className="rounded-full bg-muted-foreground/15 px-1.5 text-[11px] font-semibold text-muted-foreground">
                    {count}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      ))}
    </nav>
  );
}
